"use client";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { useMatches } from "@/hooks/useMatches";
import { useFilterOdds } from "@/hooks/useFilterOdds";
import { useMatchOdds } from "@/hooks/useMatchOdds";
import { splitCorrectScoresListToSections, formatCorrectScoresForGrid, getMatchDisplayTime } from "@/utils/matchInfo";
import { LIVE_STATUSES, MATCH_STATUS as STATUS } from "@/contants";
import { useLiveDataForMatch } from "@/hooks/useLiveData";
import { useMatchData } from "@/hooks/useMatchData";
import { formatUnixToVNDate } from "@/utils/format";
import vsIcon from "@/assets/images/vs.png";
import viugoTipsAiSparkle from "@/assets/images/viugo/kbv-v16-tips-ai-sparkle.svg";
import HBetOtherBetsDropdown from "../HBetOtherBetsDropdown/HBetOtherBetsDropdown";
import HBetKbvMatchMinute from "../HBetKbvMatchMinute";
import HBetOddsCell from "../HBetOddsCell";
import ViuGoTipsAiModal from "../ViuGoTipsAiModal";

dayjs.extend(utc);
dayjs.extend(timezone);

export default function HBetMatch({ match, leagueName, index }) {
  const [showOther, setShowOther] = useState(false);
  const [otherOddsData, setOtherOddsData] = useState(null);
  const [loadingOtherOdds, setLoadingOtherOdds] = useState(false);
  const [openTipsAiModal, setOpenTipsAiModal] = useState(false);
  const [tipsAiInsightData, setTipsAiInsightData] = useState(null);
  const [loadingTipsAiInsight, setLoadingTipsAiInsight] = useState(false);
  const { getMatchOdds, getMatchInsight } = useMatches();

  const { getOdds, getScore } = useLiveDataForMatch(match?.id);
  const liveOdds = getOdds();
  const liveScore = getScore();

  const statusNum = liveScore?.status !== undefined
    ? Number(liveScore.status)
    : Number(match.status);

  const { currentHomeScore, currentAwayScore } = useMatchData(liveScore, match);
  const isLive = LIVE_STATUSES.includes(statusNum);

  const companyIdOdds = useSelector((state) => state.bookmakerReducer);
  const filterOdds = useFilterOdds(match, liveOdds, null, companyIdOdds);

  const {
    handicapValue,
    homeHandicapOdds,
    awayHandicapOdds,
    homeHandicapTrend,
    awayHandicapTrend,
    handicapHalfValue,
    homeHandicapHalfOdds,
    awayHandicapHalfOdds,
    homeHandicapHalfTrend,
    awayHandicapHalfTrend,
    overUnderValue,
    overOdds,
    underOdds,
    overTrend,
    underTrend,
    overUnderHalfValue,
    overHalfOdds,
    underHalfOdds,
    overHalfTrend,
    underHalfTrend,
    homeEuropeOdds,
    awayEuropeOdds,
    instantDrawOdds,
    homeEuropeTrend,
    awayEuropeTrend,
    drawTrend,
    homeEuropeHalfOdds,
    awayEuropeHalfOdds,
    drawHalfOdds,
    homeEuropeHalfTrend,
    awayEuropeHalfTrend,
    drawHalfTrend,
  } = useMatchOdds(match, filterOdds, companyIdOdds);

  const homeLogo = match?.homeTeam?.logo || match?.homeLogo;
  const awayLogo = match?.awayTeam?.logo || match?.awayLogo;

  const ftHandicap = (
    <HBetOddsCell
      left={homeHandicapOdds}
      mid={handicapValue}
      right={awayHandicapOdds}
      leftTrend={homeHandicapTrend}
      rightTrend={awayHandicapTrend}
      isHandicap
    />
  );
  const ftOu = (
    <HBetOddsCell
      left={overOdds}
      mid={overUnderValue}
      right={underOdds}
      leftTrend={overTrend}
      rightTrend={underTrend}
    />
  );
  const ftEurope = (
    <HBetOddsCell
      left={homeEuropeOdds}
      mid={instantDrawOdds}
      right={awayEuropeOdds}
      leftTrend={homeEuropeTrend}
      midTrend={drawTrend}
      rightTrend={awayEuropeTrend}
      isEurope
    />
  );

  // H1 Odds
  const h1Handicap = (
    <HBetOddsCell
      left={homeHandicapHalfOdds}
      mid={handicapHalfValue}
      right={awayHandicapHalfOdds}
      leftTrend={homeHandicapHalfTrend}
      rightTrend={awayHandicapHalfTrend}
      isHandicap
    />
  );
  const h1Ou = (
    <HBetOddsCell
      left={overHalfOdds}
      mid={overUnderHalfValue}
      right={underHalfOdds}
      leftTrend={overHalfTrend}
      rightTrend={underHalfTrend}
    />
  );
  const h1Europe = (
    <HBetOddsCell
      left={homeEuropeHalfOdds}
      mid={drawHalfOdds}
      right={awayEuropeHalfOdds}
      leftTrend={homeEuropeHalfTrend}
      midTrend={drawHalfTrend}
      rightTrend={awayEuropeHalfTrend}
      isEurope
    />
  );

  const correctScoresListFull = useMemo(
    () =>
      otherOddsData?.correctScoresOdds
        ? formatCorrectScoresForGrid(otherOddsData.correctScoresOdds)
        : [],
    [otherOddsData],
  );
  const correctScoresFull = useMemo(
    () => splitCorrectScoresListToSections(correctScoresListFull),
    [correctScoresListFull],
  );
  const totalCornersOdds = useMemo(
    () =>
      Array.isArray(otherOddsData?.totalCornersOdds)
        ? otherOddsData.totalCornersOdds
        : [],
    [otherOddsData],
  );
  const hasOtherData = useMemo(
    () =>
      (otherOddsData?.goalOddEvenOdds?.length > 0) ||
      (correctScoresListFull.length > 0) ||
      (totalCornersOdds.length > 0),
    [otherOddsData, correctScoresListFull.length, totalCornersOdds.length],
  );

  const handleOtherClick = (e) => {
    e.stopPropagation();
    setShowOther((prev) => !prev);
    if (!showOther && !otherOddsData && match?.id) {
      setLoadingOtherOdds(true);
      getMatchOdds(match.id, { include: "otherOdd" })
        .then((data) => {
          if (data?.success) setOtherOddsData(data.data);
        })
        .finally(() => setLoadingOtherOdds(false));
    }
  };

  const handleOpenTipsAiModal = async (e) => {
    e?.stopPropagation?.();
    setOpenTipsAiModal(true);
    if (tipsAiInsightData || loadingTipsAiInsight || !match?.id) return;

    setLoadingTipsAiInsight(true);
    try {
      const insightResponse = await getMatchInsight(match.id);
      if (insightResponse?.success && insightResponse?.data) {
        setTipsAiInsightData(insightResponse.data);
      }
    } finally {
      setLoadingTipsAiInsight(false);
    }
  };

  return (
    <>
      <div className={`kbv-hbet-card kbv-hbet-hover ${isLive ? 'kbv-hbet-card--live' : ''} my-3 flex cursor-pointer flex-col gap-y-2 overflow-hidden pb-3 first:mt-0 md:relative md:my-0 md:flex-row md:gap-y-0 md:px-2 md:pb-0 transition-all duration-200`}>
        {/* Teams & Time Region  */}
        <div className="mb-0 mt-2 flex w-full md:my-0 md:flex-1 md:pl-0 md:px-0 px-0 flex-col justify-center">
          <div className="flex w-full flex-row items-center">
          {/* Desktop Match Time */}
            <HBetKbvMatchMinute
              matchTime={match?.matchTime}
              status={statusNum}
              liveScore={liveScore}
              halfStartTime={match?.halfStartTime}
              className={`text-[14px] ${isLive ? "text-[var(--color-up)]" : "text-[var(--theme-text-muted)]"} hidden md:mr-4 md:flex flex-shrink-0`}
              showDate
              stacked
            />

            <div className="flex-1 grid grid-cols-[1fr_auto_1fr] items-center">
              <div className="flex flex-col-reverse md:flex-row items-center justify-end gap-y-1 md:gap-y-0 md:gap-x-2 min-w-0">
                <span className="text-[var(--theme-text-muted)] text-[13px] md:text-[14px] font-bold text-center md:text-right whitespace-normal md:truncate leading-tight">
                  {match?.homeName}
                </span>
                <img className="h-6 w-6 object-contain flex-shrink-0" src={homeLogo || ""} alt={match?.homeName} />
              </div>

              {/* VS/Score */}
              <div className="flex items-center justify-center shrink-0 w-[42px] md:w-[50px]">
                {isLive && currentHomeScore !== null && currentAwayScore !== null ? (
                  <span className="text-[var(--color-up)] text-[16px] md:text-[18px] font-bold whitespace-nowrap">
                    {currentHomeScore} - {currentAwayScore}
                  </span>
                ) : (
                  <img loading="lazy" height="14" width="24" className="h-[14px] w-6 shrink-0" src={vsIcon} alt="vs" />
                )}
              </div>

              <div className="flex flex-col md:flex-row items-center justify-start gap-y-1 md:gap-y-0 md:gap-x-2 min-w-0">
                <img className="h-6 w-6 object-contain flex-shrink-0" src={awayLogo || ""} alt={match?.awayName} />
                <span className="text-[var(--theme-text-muted)] text-[13px] md:text-[14px] font-bold text-center md:text-left whitespace-normal md:truncate leading-tight">
                  {match?.awayName}
                </span>
              </div>
            </div>
          </div>
          
          <div className="hidden md:block md:h-[34px] md:mt-0 mb-0" />
        </div>

        {/* Mobile Match Time */}
        <HBetKbvMatchMinute
          matchTime={match?.matchTime}
          status={statusNum}
          liveScore={liveScore}
          halfStartTime={match?.halfStartTime}
          className={`flex w-full flex-row items-center justify-center gap-x-1 md:hidden text-[14px] ${isLive ? "text-[var(--color-up)]" : "text-[var(--theme-text-muted)]"}`}
          showDate
        />

        {/* Odds + Other button */}
        <div className="flex w-full flex-col md:mt-0 md:h-fit md:flex-1 md:items-end">
          <span className="w-full grid grid-cols-[10px_1fr_1fr_1fr] md:grid-cols-[18px_140px_140px_140px] gap-x-1 px-1 md:gap-x-2 md:px-0 md:py-2 md:ml-auto md:pt-3">
            <span className="flex flex-col items-center justify-start pt-[26px] md:pt-0 md:justify-center text-[10px] text-[var(--theme-text-muted)] opacity-50 font-normal">
              <span className="h-8 flex items-center justify-center mb-1">FT</span>
              <span className="w-full h-px bg-[var(--theme-text-muted)] opacity-10 my-0.5" />
              <span className="h-8 flex items-center justify-center mt-1">HT</span>
            </span>

            <span className="flex flex-col gap-y-1 md:gap-y-2">
              <span className="text-[var(--theme-text)] text-[13px] flex items-center justify-center font-bold md:hidden">
                Kèo chấp
              </span>
              {ftHandicap}
              <span className="w-full h-px bg-[var(--theme-text-muted)] opacity-10 my-0.5 md:hidden" />
              {h1Handicap}
            </span>
            <span className="flex flex-col gap-y-1 md:gap-y-2">
              <span className="text-[var(--theme-text)]  text-[13px] flex items-center justify-center font-bold md:hidden">
                Tài/Xỉu
              </span>
              {ftOu}
              <span className="w-full h-px bg-[var(--theme-text-muted)] opacity-10 my-0.5 md:hidden" />
              {h1Ou}
            </span>
            <span className="flex flex-col gap-y-1 md:gap-y-2">
              <span className="text-[var(--theme-text)]  text-[13px] flex items-center justify-center font-bold md:hidden">
                1X2
              </span>
              {ftEurope}
              <span className="w-full h-px bg-[var(--theme-text-muted)] opacity-10 my-0.5 md:hidden" />
              {h1Europe}
            </span>
          </span>

          {/* Cược khác Button */}
          <div className="md:mb-2 flex w-full items-center justify-center gap-2 mt-2 px-2 md:px-0 md:mt-0.5 md:justify-end">
            <button
              type="button"
              onClick={handleOpenTipsAiModal}
              className="md:hidden kbv-tips-btn-indigo-slate relative flex h-8 w-[140px] shrink-0 cursor-pointer items-center justify-center gap-0.5 whitespace-nowrap rounded-[8px] border border-solid px-2 text-[11px] font-medium leading-[15px] text-white"
            >
              <img
                src={viugoTipsAiSparkle}
                alt=""
                width={16}
                height={16}
                className="h-3.5 w-3.5 shrink-0"
                aria-hidden
              />
              <div>Tips AI</div>
            </button>
            <button
              onClick={handleOtherClick}
              className="flex flex-row items-center justify-center md:hover:cursor-pointer bg-[linear-gradient(180deg,_#ffca43,_#e53c01)] text-[var(--theme-text)] active:text-[var(--theme-text)] text-[12px] md:text-[14px] h-8 md:h-[34px] w-[140px] md:w-auto md:min-w-[100px] md:px-4 rounded-lg font-bold md:shrink-0 whitespace-nowrap"
              type="button"
            >
              Cược khác
            </button>
          </div>
        </div>

        {/* Tips AI button */}
        <div className="hidden md:flex absolute bottom-3 left-2 z-10">
          <button
            type="button"
            onClick={handleOpenTipsAiModal}
            className="kbv-tips-btn-indigo-slate relative flex h-[26px] w-max max-w-full shrink-0 cursor-pointer items-center justify-center gap-0.5 whitespace-nowrap rounded-[8px] border border-solid p-[3px] text-[11px] font-medium leading-[15px] text-white max-lg:w-max lg:h-auto lg:gap-1 lg:px-3 lg:py-2 lg:text-[12px]"
          >
            <img
              src={viugoTipsAiSparkle}
              alt=""
              width={16}
              height={16}
              className="h-3.5 w-3.5 shrink-0 lg:h-4 lg:w-4"
              aria-hidden
            />
            <div>Tips AI</div>
          </button>
        </div>
      </div>

      {showOther && (
        <HBetOtherBetsDropdown
          loadingOtherOdds={loadingOtherOdds}
          otherOddsData={otherOddsData}
          hasOtherData={hasOtherData}
          correctScoresListFull={correctScoresListFull}
          correctScoresFull={correctScoresFull}
          totalCornersOdds={totalCornersOdds}
          odds={{
            ...filterOdds,
            homeEuropeOdds,
            awayEuropeOdds,
            instantDrawOdds,
            homeEuropeTrend,
            awayEuropeTrend,
            drawTrend,
            homeHandicapHalfOdds,
            awayHandicapHalfOdds,
            homeHandicapHalfTrend,
            awayHandicapHalfTrend,
          }}
          showOther={showOther}
        />
      )}

      <ViuGoTipsAiModal
        open={openTipsAiModal}
        onClose={() => setOpenTipsAiModal(false)}
        match={match}
        matchInfo={{
          ...formatUnixToVNDate(match?.matchTime),
          isLive,
        }}
        statsInfo={{
          currentHomeScore,
          currentAwayScore,
          homeYellow: null,
          homeRed: null,
          awayYellow: null,
          awayRed: null,
          currentHomeCorner: null,
          currentAwayCorner: null,
        }}
        oddsInfo={{
          handicapValue,
          homeHandicapOdds,
          awayHandicapOdds,
          overUnderValue,
          overOdds,
          underOdds,
          homeEuropeOdds,
          awayEuropeOdds,
          instantDrawOdds,
          handicapHalfValue,
          homeHandicapHalfOdds,
          awayHandicapHalfOdds,
        }}
        leagueName={match?.league?.name || match?.leagueName || leagueName}
        leagueLogo={match?.league?.logo || match?.leagueLogo || ""}
        homeName={match?.homeName}
        homeLogo={homeLogo || ""}
        awayName={match?.awayName}
        awayLogo={awayLogo || ""}
        matchDate={formatUnixToVNDate(match?.matchTime)?.date}
        matchTime={formatUnixToVNDate(match?.matchTime)?.gio}
        matchTimestamp={match?.matchTime}
        headerScore={`${currentHomeScore ?? 0} - ${currentAwayScore ?? 0}`}
        liveScoreToday={liveScore}
        matchStatusNum={statusNum}
        matchTimeUnix={match?.matchTime}
        halfStartTime={match?.halfStartTime}
        isMatchLive={isLive}
        statusLabel={
          !isLive
            ? "Sắp diễn ra"
            : (() => {
                const dt = getMatchDisplayTime(
                  liveScore,
                  statusNum,
                  match?.matchTime,
                  match?.halfStartTime,
                );
                if (dt?.type === "minute") {
                  return dt.halfLabel && dt.halfLabel !== "--" ? dt.halfLabel : "Trực tiếp";
                }
                if (dt?.type === "var") return "VAR";
                return dt?.content ?? "Trực tiếp";
              })()
        }
        insightData={tipsAiInsightData}
        insightLoading={loadingTipsAiInsight}
      />
    </>
  );
}
